import {
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Button,
  Theme,
  makeStyles,
  createStyles,
  Grid,
  Typography,
} from '@material-ui/core';
import React, { useState } from 'react';
import { Person, PayerPaymentMethod, PaymentMethodType, PaymentMethodHolder, PaymentMethodHolderInput } from '../gql-types.generated';
import BillingEdit from './BillingEdit';
import CardInfo from './CardInfo';
import PaymentMethodEdit from './PaymentMethodEdit';
import AddIcon from '@material-ui/icons/Add';
import { submitPaymentMethod, tokenizeCCInfo } from '../util/WePay';
import { PaymentMethodStatusData } from '../features/multipleMethodsHome/MultipleMethodsHome';
import { WePayTokenResponse } from '../custom-types/WePayTokenResponse';
import { checkIsFullNameValid } from '../util/Validators';
import LoadingMask from './LoadingMask';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

interface PaymentMethodAddProps {
  cancel: () => void;
  continueText: string;
  isReplace: boolean;
  onMobile: boolean;
  payerProfileHolderInfo?: PaymentMethodHolder | null;
  selectedPaymentMethod?: PayerPaymentMethod;
  submit: (
    type: PaymentMethodType,
    token: string,
    attachToResourceId: string | undefined,
    holder: PaymentMethodHolderInput,
    isDefault: boolean,
  ) => void;
  viewerUser: Person | undefined;
  paymentMethods: PayerPaymentMethod[] | null | undefined;
  addPaymentMethodType?: PaymentMethodType;
  billingAddresses?: PaymentMethodHolder[] | null;
  addBillingAddress?: (billingAddress: PaymentMethodHolder) => void;
  paymentMethodAddStep: number;
  setPaymentMethodAddStep: (step: number) => void;
  isIFrameVisible: boolean;
  setIsIFrameVisible: (isIFrameVisible: boolean) => void;
  loadingPaymentMethods: boolean;
  addPaymentMethodError?: Error;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    radioLabel: {
      paddingTop: theme.spacing(1.5),
    },
    buttonContainer: {
      padding: theme.spacing(1),
      width: '100%',
    },
    fieldsetContent: {
      minWidth: 400,
      width: '100%',
    },
    cancelButtonItem: {
      margin: theme.spacing(0, 1, 0, 0),
    },
    maximumError: {
      fontWeight: 500,
      fontSize: '1rem',
      display: 'block',
      padding: theme.spacing(0, 0, 0, 2),
      margin: theme.spacing(2, 0, 0, 0),
    },
    padding: {
      padding: theme.spacing(1),
    },
    displayNone: {
      display: 'none',
    },
    radio: {
      alignSelf: 'flex-start',
      paddingTop: theme.spacing(0.5),
    },
    addressFont: {
      fontSize: '13px',
    },
    numberFont: {
      fontSize: '12px',
    },
    errorGrid: {
      backgroundColor: '#FFF7F7',
      padding: theme.spacing(1, 3),
    },
    errorText: {
      marginLeft: theme.spacing(1),
      maxWidth: '80%',
    },
    dialogPadding: {
      padding: theme.spacing(0, 2, 1),
    },
  }),
);
const PaymentMethodAdd: React.FC<PaymentMethodAddProps> = props => {
  const classes = useStyles();
  const {
    cancel,
    onMobile,
    viewerUser,
    submit,
    addPaymentMethodType,
    payerProfileHolderInfo,
    billingAddresses,
    addBillingAddress,
    paymentMethodAddStep,
    setPaymentMethodAddStep,
    paymentMethods,
    isIFrameVisible,
    setIsIFrameVisible,
    loadingPaymentMethods,
    addPaymentMethodError,
  } = props;
  const [activeBankInfo, setActiveBankInfo] =
    useState<{
      holderName: string;
      accountNumber: string;
      routingNumber: string;
      accountType: string;
    }>();
  const [tokenizeIFrame, setTokenizeIFrame] = useState<{ tokenize: () => Promise<WePayTokenResponse> }>();
  const [paymentStatusData, setPaymentStatusData] = useState<PaymentMethodStatusData>({} as PaymentMethodStatusData);
  const [creditError, setCreditError] = useState<Error>();
  const [selectedBillingAddress, setSelectedBillingAddress] = useState<PaymentMethodHolder | null | undefined>(payerProfileHolderInfo);
  const [selectedRadio, setSelectedRadio] = useState<string | null | undefined>(
    payerProfileHolderInfo
      ? `${payerProfileHolderInfo.address?.line1}~${payerProfileHolderInfo.address?.postalCode}~${payerProfileHolderInfo.email}~${payerProfileHolderInfo.phone?.number}`
      : '',
  );
  const [isAccountNumberValid, setIsAccountNumberValid] = useState(true);
  const [cardHolderName, setCardHolderName] = useState<string>('');
  const [accountHolderNameValid, setAccountHolderNameValid] = useState(true);
  const [cardHolderNameValid, setCardHolderNameValid] = useState(true);

  const handleAddBillingClick = () => {
    setPaymentMethodAddStep(1);
  };

  const handleCancelBillingClick = () => {
    setPaymentMethodAddStep(0);
  };

  const handleFinishAddingBilling = (billingAddress: PaymentMethodHolder) => {
    const isDuplicated = billingAddresses?.find(address => {
      return (
        address.address?.line1 &&
        address.address?.postalCode &&
        address.email &&
        address.phone?.number &&
        address.address?.line1.localeCompare(billingAddress.address?.line1 || '') === 0 &&
        address.address?.postalCode.localeCompare(billingAddress.address?.postalCode || '') === 0 &&
        address.email.localeCompare(billingAddress?.email || '') === 0 &&
        address.phone.number.localeCompare(billingAddress.phone?.number || '') === 0
      );
    });

    if (addBillingAddress && !isDuplicated) {
      addBillingAddress(billingAddress);
    }
    setSelectedBillingAddress(billingAddress);
    setSelectedRadio(
      `${billingAddress.address?.line1}~${billingAddress.address?.postalCode}~${billingAddress.email}~${billingAddress.phone?.number}`,
    );
    setPaymentMethodAddStep(0);
  };

  const continueSavePaymentMethod = (type: PaymentMethodType, token: string, holderName: string) => {
    if (
      selectedBillingAddress &&
      selectedBillingAddress.address &&
      selectedBillingAddress.address.postalCode &&
      selectedBillingAddress.address.country &&
      selectedBillingAddress.phone &&
      selectedBillingAddress.phone.countryCode &&
      selectedBillingAddress.phone.number
    ) {
      submit(
        type,
        token,
        viewerUser?.id,
        {
          email: selectedBillingAddress.email,
          name: holderName,
          address: {
            line1: selectedBillingAddress.address?.line1,
            line2: selectedBillingAddress.address?.line2,
            postalCode: selectedBillingAddress.address.postalCode,
            country: selectedBillingAddress.address.country,
            region: '',
            city: '',
          },
          phone: {
            countryCode: selectedBillingAddress?.phone?.countryCode,
            number: selectedBillingAddress?.phone?.number,
          },
        },
        paymentMethods ? paymentMethods.length === 0 : false,
      );
    }
  };

  const onTokenizeSuccess = (token: string) => {
    if (token) {
      continueSavePaymentMethod(PaymentMethodType.CreditCard, token, cardHolderName);
    }
  };
  const onTokenizeFailure = (error: Error) => {
    if (error) {
      setCreditError(error as Error);
    }
  };
  const submitBankFailure = (statusData: PaymentMethodStatusData) => {
    setPaymentStatusData(statusData);
  };
  const submitBankSuccess = (token: string) => {
    continueSavePaymentMethod(PaymentMethodType.PaymentBankUs, token, activeBankInfo?.holderName || '');
  };
  const checkBankAccountNumber = () => {
    if (!!activeBankInfo?.accountNumber && activeBankInfo.accountNumber.length >= 3 && activeBankInfo.accountNumber.length <= 17) {
      setIsAccountNumberValid(true);
    } else {
      setIsAccountNumberValid(false);
    }
  };
  const handleSavePaymentMethod = () => {
    if (addPaymentMethodType === PaymentMethodType.CreditCard && tokenizeIFrame) {
      setCardHolderNameValid(checkIsFullNameValid(cardHolderName));
      if (checkIsFullNameValid(cardHolderName)) tokenizeCCInfo(tokenizeIFrame.tokenize, onTokenizeSuccess, onTokenizeFailure);
    } else if (addPaymentMethodType === PaymentMethodType.PaymentBankUs) {
      setPaymentStatusData({} as PaymentMethodStatusData);
      checkBankAccountNumber();
      if (activeBankInfo) setAccountHolderNameValid(checkIsFullNameValid(activeBankInfo.holderName));
      if (activeBankInfo && selectedBillingAddress && isAccountNumberValid && checkIsFullNameValid(activeBankInfo.holderName)) {
        submitPaymentMethod(activeBankInfo, selectedBillingAddress, submitBankSuccess, submitBankFailure, true);
      }
    }
  };

  const onBankInfoChange = (bankInfo: { holderName: string; accountNumber: string; routingNumber: string; accountType: string }) => {
    setActiveBankInfo(bankInfo);
  };

  const onTokenizeInjectSuccess = (iFrame: { tokenize: () => Promise<WePayTokenResponse> }) => {
    if (iFrame) {
      setTokenizeIFrame(iFrame);
    }
  };

  const handleSelectRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRadio(event.target.value);
    const addressValues = event.target.value.split('~');
    setSelectedBillingAddress(
      billingAddresses?.find(address => {
        return (
          addressValues.length === 4 &&
          address.address?.line1 &&
          address.address?.postalCode &&
          address.email &&
          address.phone?.number &&
          address.address?.line1.localeCompare(addressValues[0]) === 0 &&
          address.address?.postalCode.localeCompare(addressValues[1]) === 0 &&
          address.email.localeCompare(addressValues[2]) === 0 &&
          address.phone.number.localeCompare(addressValues[3]) === 0
        );
      }),
    );
  };

  const selectBillingRadio = (
    <FormControl>
      <RadioGroup color="primary" name="selectedBillingAddress" value={selectedRadio} onChange={handleSelectRadio}>
        {billingAddresses &&
          billingAddresses.map((address, index) => {
            return (
              <FormControlLabel
                key={index}
                value={`${address.address?.line1}~${address.address?.postalCode}~${address.email}~${address.phone?.number}`}
                control={<Radio size="small" color="primary" className={classes.radio} />}
                label={
                  <Grid item container direction="column">
                    <Typography variant="subtitle2">{address.email}</Typography>
                    <Typography className={classes.addressFont}>{`${address?.address?.line1} ${
                      address?.address?.line2 || ''
                    }`}</Typography>
                    <Typography
                      className={classes.addressFont}
                    >{`${address?.address?.country}, ${address?.address?.postalCode}`}</Typography>
                    <Typography color="textSecondary" className={classes.numberFont}>{`${
                      address?.phone?.countryCode
                    } (${address?.phone?.number?.slice(0, 3)}) ${address?.phone?.number?.slice(3, 6)}-${address?.phone?.number?.slice(
                      -4,
                    )}`}</Typography>
                  </Grid>
                }
                className={classes.radioLabel}
                data-cy="address-details"
              />
            );
          })}
      </RadioGroup>
      {((paymentMethods && billingAddresses && paymentMethods?.length < 5 && billingAddresses?.length < 5) ||
        (paymentMethods && billingAddresses && paymentMethods?.length === 5 && billingAddresses?.length < 6) ||
        (!paymentMethods && billingAddresses && billingAddresses.length < 5) ||
        (!paymentMethods && !billingAddresses)) && (
        <Grid container item xs={12}>
          <Button variant="text" color="primary" startIcon={<AddIcon />} onClick={handleAddBillingClick} data-cy="add-address">
            ADD ADDRESS
          </Button>
        </Grid>
      )}
    </FormControl>
  );

  return (
    <Box data-cy="payment-method-add">
      <LoadingMask loading={loadingPaymentMethods} />
      {/* Show this error when there has been an error while creating a payment method */}
      {addPaymentMethodError && (
        <Grid item container xs={12} className={classes.errorGrid} alignItems="center">
          <ErrorOutlineIcon color="error" fontSize="small" />
          <Typography color="error" className={classes.errorText} variant="subtitle2">
            {addPaymentMethodError.message}
          </Typography>
        </Grid>
      )}
      <Grid xs={12} direction="column" className={paymentMethodAddStep === 1 ? classes.displayNone : classes.dialogPadding}>
        <Grid item xs={12}>
          {addPaymentMethodType === PaymentMethodType.CreditCard && (
            <CardInfo
              creditError={creditError}
              onTokenizeInjectSuccess={onTokenizeInjectSuccess}
              viewerUser={viewerUser}
              holderName={cardHolderName}
              setHolderName={setCardHolderName}
              holderNameValid={cardHolderNameValid}
              setHolderNameValid={setCardHolderNameValid}
              isIFrameVisible={isIFrameVisible}
              setIsIFrameVisible={setIsIFrameVisible}
            />
          )}
          {addPaymentMethodType === PaymentMethodType.PaymentBankUs && (
            <PaymentMethodEdit
              onBankInfoChange={onBankInfoChange}
              statusData={paymentStatusData}
              onMobile={onMobile}
              paymentMethodExists={false}
              isAccountNumberValid={isAccountNumberValid}
              holderNameValid={accountHolderNameValid}
              setHolderNameValid={setAccountHolderNameValid}
            />
          )}
        </Grid>

        <Grid container item xs={12}>
          <Grid container item xs={10} alignItems="center">
            <Typography color="textSecondary">Billing Addresses</Typography>
          </Grid>
        </Grid>

        <Grid container item justify="space-between" xs={10}>
          <Grid container item direction="column" xs={12}>
            {selectBillingRadio}
          </Grid>
        </Grid>

        <Grid item container xs={12} justify="flex-end" className={classes.padding}>
          <Button
            className={classes.cancelButtonItem}
            variant="text"
            color="primary"
            onClick={() => {
              cancel();
            }}
            data-cy="cancel"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!selectedBillingAddress}
            onClick={() => {
              handleSavePaymentMethod();
            }}
            data-cy="continue-to-payment"
          >
            Save
          </Button>
        </Grid>
      </Grid>
      <Grid className={paymentMethodAddStep === 0 ? classes.displayNone : classes.dialogPadding}>
        <BillingEdit handleBack={handleCancelBillingClick} handleContinue={handleFinishAddingBilling} isAdd={true} onMobile={false} />
      </Grid>
    </Box>
  );
};

export default PaymentMethodAdd;
